import { Navigate, useParams } from "react-router-dom";

import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { ROUTE_PATH } from "src/core/constants/routePath";
import { DocumentPreviewProvider } from "src/core/contexts/DocumentPreviewContext";
import { FolderProvider } from "src/core/contexts/FolderContext";
import { FolderGraphExportProvider } from "src/core/contexts/FolderGraphExportContext";
import { FolderGraphSyncProvider } from "src/core/contexts/FolderGraphSyncContext";
import { RiskOptionsProvider } from "src/core/contexts/RiskOptionsContext";
import { useFolderFileQuery } from "src/core/gql/graphql";
import { useFolderInitialization } from "src/core/hooks/folder/useFolderInitialization";
import { useTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { withRequiredOverride } from "src/core/overrides";

const FolderContent = withRequiredOverride("components.folder.FolderContent");

export const Folder = () => {
  const { id } = useParams<{ id: string }>();
  const tenant = useTenantPaths();
  const to404 = tenant?.error404() ?? ROUTE_PATH.ERROR_404;

  const { data, loading, error, refetch } = useFolderFileQuery({
    variables: { id: id ?? "" },
    skip: !id,
  });

  const { isInitializing, isQuestionnaireReady } = useFolderInitialization(data, refetch);

  // Si pas d'id => 404
  if (!id) {
    return <Navigate to={to404} replace />;
  }

  // loading Apollo uniquement
  if (loading) {
    return <SpinnerFullPage />;
  }

  // erreur réseau/graphql ou entity non trouvée => 404
  if (error || !data?.folderFile) {
    return <Navigate to={to404} replace />;
  }

  if (isInitializing || !isQuestionnaireReady) {
    return <SpinnerFullPage />;
  }

  return (
    <RiskOptionsProvider>
      <FolderProvider folderFile={data} refetch={refetch}>
        <FolderGraphSyncProvider>
          <FolderGraphExportProvider>
            <DocumentPreviewProvider>
              <FolderContent />
            </DocumentPreviewProvider>
          </FolderGraphExportProvider>
        </FolderGraphSyncProvider>
      </FolderProvider>
    </RiskOptionsProvider>
  );
};
